/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Material Dashboard 2 PRO React TSUI Dashboard PRO Material.
 * You can add new color using this file.
 * You can customized the colors for the entire Material Dashboard 2 PRO React TSUI Dashboard PRO Material using thie file.
 */

// types
interface ColorsTypes {
  main: string;
  focus: string;
}

interface GradientsTypes {
  main: string;
  state: string;
}

interface SocialMediaColorsTypes {
  main: string;
  dark: string;
}

interface BadgeColorsTypes {
  background: string;
  text: string;
}

interface Types {
  background:
    | {
        default: string;
        sidenav?: string;
        card?: string;
      }
    | any;
  white:
    | {
        main: string;
        focus: string;
      }
    | any;
  text:
    | {
        main: string;
        focus: string;
        primary?: string;
        secondary?: string;
        disabled?: string;
      }
    | any;
  transparent:
    | {
        main: string;
      }
    | any;
  black: {
    light: string;
    main: string;
    focus: string;
  };

  primary: ColorsTypes | any;
  secondary: ColorsTypes | any;
  info: ColorsTypes | any;
  success: ColorsTypes | any;
  warning: ColorsTypes | any;
  error: ColorsTypes | any;
  light: ColorsTypes | any;
  dark: ColorsTypes | any;
  grey:
    | {
        [key: string | number]: string;
      }
    | any;
  gray: {
    [key: string | number]: string;
  };
  gradients:
    | {
        primary: GradientsTypes;
        secondary: GradientsTypes;
        info: GradientsTypes;
        success: GradientsTypes;
        warning: GradientsTypes;
        error: GradientsTypes;
        light: GradientsTypes;
        dark: GradientsTypes;
        black: GradientsTypes;
      }
    | any;
  socialMediaColors:
    | {
        facebook: SocialMediaColorsTypes;
        twitter: SocialMediaColorsTypes;
        instagram: SocialMediaColorsTypes;
        linkedin: SocialMediaColorsTypes;
        pinterest: SocialMediaColorsTypes;
        youtube: SocialMediaColorsTypes;
        vimeo: SocialMediaColorsTypes;
        slack: SocialMediaColorsTypes;
        dribbble: SocialMediaColorsTypes;
        github: SocialMediaColorsTypes;
        reddit: SocialMediaColorsTypes;
        tumblr: SocialMediaColorsTypes;
      }
    | any;
  badgeColors:
    | {
        primary: BadgeColorsTypes;
        secondary: BadgeColorsTypes;
        info: BadgeColorsTypes;
        success: BadgeColorsTypes;
        warning: BadgeColorsTypes;
        error: BadgeColorsTypes;
        light: BadgeColorsTypes;
        dark: BadgeColorsTypes;
      }
    | any;
  coloredShadows:
    | {
        [key: string]: string;
      }
    | any;
  inputBorderColor: string;
  tabs:
    | {
        indicator:
          | {
              boxShadow: string;
            }
          | any;
      }
    | any;

  soundLevelDisconnected: {
    main: string;
  };
  soundLevelSilence: {
    main: string;
  };
  soundLevelLow: {
    main: string;
  };
  soundLevelMedium: {
    main: string;
  };
  soundLevelHigh: {
    main: string;
  };
  iaqExcellent: {
    main: string;
  };
  iaqGood: {
    main: string;
  };
  iaqLightlyPolluted: {
    main: string;
  };
  iaqModeratelyPolluted: {
    main: string;
  };
  iaqHeavilyPolluted: {
    main: string;
  };
  iaqSeverelyPolluted: {
    main: string;
  };
  iaqExtremelyPolluted: {
    main: string;
  };
  icons: {
    background: string;
  };
  card: {
    divider: string;
  };
  occupancy: {
    low: {
      main: string;
    };
    medium: {
      main: string;
    };
    high: {
      main: string;
    };
    full: {
      main: string;
    };
    empty: {
      main: string;
    };
  };
  occupancyCardLevelText: {
    main: string;
  };
  barChartTooltip: {
    backgroundColor: string;
  };
  barChartGridColor: {
    light: string;
  };
  barChartBarHover: {
    main: string;
  };
  lightGreen: {
    main: string;
  };
  darkGreen: {
    main: string;
  };
  battery: {
    low: {
      main: string;
    };
  };
  darkGrey: {
    main: string;
  };
  lightGrey: {
    main: string;
  };
  borderGrey: {
    main: string;
  };
  descriptionGrey: {
    main: string;
  };
  linearGrey: {
    main: string;
  };
  tooltip: {
    backgroundColor: string;
  };
  borderLightRed: {
    main: string;
  };
  backgroundLightRed: {
    main: string;
  };
  backgroundLightOrange: {
    main: string;
  };
  mediumGrey: {
    main: string;
  };
  annotationBorderColor: {
    main: string;
  };
  green: {
    [key: number]: string;
  };
  orange: {
    [key: number]: string;
  };
  red: {
    [key: number]: string;
  };
  blue: {
    [key: number]: string;
  };
}

const colors: Types = {
  iaqExcellent: {
    main: "#00e400",
  },
  iaqGood: {
    main: "#92d050",
  },
  iaqLightlyPolluted: {
    main: "#ffff00",
  },
  iaqModeratelyPolluted: {
    main: "#ff7e00",
  },
  iaqHeavilyPolluted: {
    main: "#ff0000",
  },
  iaqSeverelyPolluted: {
    main: "#99004c",
  },
  iaqExtremelyPolluted: {
    main: "#663300",
  },
  soundLevelDisconnected: {
    main: "#adb5bd",
  },
  soundLevelSilence: {
    main: "#4CAF50",
  },
  soundLevelLow: {
    main: "#ff8a65",
  },
  soundLevelMedium: {
    main: "#ff5722",
  },
  soundLevelHigh: {
    main: "#bf360c",
  },
  background: {
    default: "#f0f2f5",
  },

  text: {
    main: "#7b809a",
    focus: "#7b809a",
  },

  transparent: {
    main: "#00000000",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  primary: {
    main: "#1A73E8",
    focus: "#1662C4",
  },

  secondary: {
    main: "#7b809a",
    focus: "#8f93a9",
  },

  info: {
    main: "#1A73E8",
    focus: "#1662C4",
  },

  success: {
    main: "#4CAF50",
    focus: "#67bb6a",
  },

  warning: {
    main: "#fb8c00",
    focus: "#fc9d26",
  },

  error: {
    main: "#F44335",
    focus: "#f65f53",
  },

  light: {
    main: "#f0f2f5",
    focus: "#f0f2f5",
  },

  dark: {
    main: "#344767",
    focus: "#2c3c58",
  },

  grey: {
    100: "#f8f9fa",
    200: "#f0f2f5",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },
  gray: {
    0: "#F8F9FA",
    1: "#F1F3F5",
    2: "#FAFAFA",
    5: "#ADB5BD",
    6: "#868E96",
    7: "#495057",
    9: "#343A40",
    10: "#212121",
  },

  green: {
    2: "#ECFFF4",
    5: "#0CAB4F",
    7: "#608670",
    9: "#52684A",
  },

  orange: {
    1: "#FBF2ED",
    5: "#FFAE80",
    7: "#CA5A1B",
  },

  red: {
    1: "#FDF0EF",
    2: "#FFF5F7",
    7: "#C92A2A",
  },

  blue: {
    1: "#DBEDFC",
    5: "#49A3F1",
  },

  gradients: {
    primary: {
      main: "#49a3f1",
      state: "#1A73E8",
    },

    secondary: {
      main: "#747b8a",
      state: "#495361",
    },

    info: {
      main: "#49a3f1",
      state: "#1A73E8",
    },

    success: {
      main: "#66BB6A",
      state: "#43A047",
    },

    warning: {
      main: "#FFA726",
      state: "#FB8C00",
    },

    error: {
      main: "#EF5350",
      state: "#E53935",
    },

    light: {
      main: "#EBEFF4",
      state: "#CED4DA",
    },

    dark: {
      main: "#42424a",
      state: "#191919",
    },

    black: {
      main: "#000000",
      state: "#495057",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  badgeColors: {
    primary: {
      background: "#aecef7",
      text: "#095bc6",
    },

    secondary: {
      background: "#d7d9e1",
      text: "#6c757d",
    },

    info: {
      background: "#aecef7",
      text: "#095bc6",
    },

    success: {
      background: "#bce2be",
      text: "#339537",
    },

    warning: {
      background: "#ffd59f",
      text: "#c87000",
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: "#8097bf",
      text: "#1e2e4a",
    },
  },

  coloredShadows: {
    primary: "#00bbd4",
    secondary: "#110e0e",
    info: "#00bbd4",
    success: "#4caf4f",
    warning: "#ff9900",
    error: "#f44336",
    light: "#adb5bd",
    dark: "#404040",
  },

  inputBorderColor: "#d2d6da",

  tabs: {
    indicator: { boxShadow: "#ddd" },
  },

  icons: {
    background: "#34476714",
  },
  card: {
    divider: "#E9E9E9",
  },
  occupancy: {
    low: {
      main: "#B9DEFF",
    },
    medium: {
      main: "#6ABAFF",
    },
    high: {
      main: "#FFAE80",
    },
    full: {
      main: "#C92A2A",
    },
    empty: {
      main: "#aaaaaa",
    },
  },
  occupancyCardLevelText: {
    main: "#909499",
  },
  barChartTooltip: {
    backgroundColor: "#616161F2",
  },
  barChartGridColor: {
    light: "#F1F1F1",
  },
  barChartBarHover: {
    main: "#AC1919",
  },
  lightGreen: {
    main: "#12D6000A",
  },
  darkGreen: {
    main: "#52684A",
  },
  battery: {
    low: {
      main: "#CA5A1B",
    },
  },
  darkGrey: {
    main: "#5F646C",
  },
  lightGrey: {
    main: "#F9F9F9",
  },
  borderGrey: {
    main: "#D6D6D6",
  },
  descriptionGrey: {
    main: "#5B5E64",
  },
  linearGrey: {
    main: "#7B809A",
  },
  tooltip: {
    backgroundColor: "#616161E5",
  },
  borderLightRed: {
    main: "#FFC5C5",
  },
  backgroundLightOrange: {
    main: "#FFF6F1",
  },
  backgroundLightRed: {
    main: "#FA1E1E14",
  },
  mediumGrey: {
    main: "#969696",
  },
  annotationBorderColor: {
    main: "#B8B8B8",
  },
};

export default colors;
